import {UIElementApi} from '../api/UIElementApi';
import {BaseValidatedClass} from '../BaseValidatedClass';

export class UIElement extends BaseValidatedClass {
  /** Provides access to editor functionalities specific to this UI element instance. */
  api!: UIElementApi;

  /** List of methods that must be implemented by subclasses */
  private static readonly REQUIRED_METHODS = ['onRender', 'getId', 'getTemplate'];

  constructor() {
    super(UIElement.REQUIRED_METHODS, UIElement);
  }

  /**
   * Called when the UI element should render its content into the provided container.
   * @param container - The HTMLElement where the UI element should be rendered.
   */
  public onRender(_container: HTMLElement): void {
    throw new Error('Method onRender() must be implemented by the subclass');
  }

  /**
   * Optional cleanup hook called when the UI element is being destroyed.
   * Use this to remove event listeners or perform other cleanup tasks.
   */
  onDestroy(): void {}

  /**
   * Optional method to get the current value of the UI element.
   * Implement this if the element manages a state or value (e.g., input fields).
   * @returns The current value of the element.
   */
  getValue(): any {};

  /**
   * Optional method to set the value of the UI element.
   * Implement this if the element manages a state or value and needs to be updated externally.
   * @param value - The new value to set.
   */
  setValue(_value: any): void {};

  /**
   * @description Optional hook called when one of the element's supported attributes ({@link UEAttr}) gets updated externally.
   * Implement this to react to attribute changes (e.g., visibility, disabled state).
   * @param name - The name of the attribute that was updated.
   * @param value - The new value of the attribute.
   */
  onAttributeUpdated(_name: string, _value: unknown): void {};

  /**
   * Gets the unique identifier for this UI element type.
   * This ID is used for registration and referencing within controls.
   * @returns A unique string ID.
   */
  public getId(): string {
    throw new Error('Method getId() must be implemented by the subclass');
  }

  /**
   * Gets the HTML template string that defines the structure of this UI element.
   * @returns An HTML string.
   */
  public getTemplate(): string {
    throw new Error('Method getTemplate() must be implemented by the subclass');
  }
}
